import { PartnerSettingValue } from '@provision/entity-types';
import {
  ApolloError,
  gql,
  QueryHookOptions,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNonNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { isStringBooleanTrue } from 'src/utils/strings';

export enum PartnerSetting {
  DisabledConversationUrgencyLevels = 'disabled_conversation_urgency_levels',
  DisabledConversationAssigneeGroups = 'disabled_conversation_assignee_groups',
  AllowedExternalConversationParticipantEntityPartners = 'allowed_external_conversation_participant_entity_partners',
  DisabledBehaviors = 'disabled_behaviors',
  IterableSyncOn = 'iterable_sync_on',
  DisabledMacros = 'disabled_macros',
  DisabledCustomViews = 'disabled_custom_views',
  PostHogOn = 'posthog_on',
  AnsweringServicesOn = 'answering_services_on',
  DisabledProvisionProjects = 'disabled_provision_projects',
  UsesProvisionForSupportOn = 'uses_provision_for_support',
  AiConversationSummaryEnabled = 'ai_conversation_summary_enabled',
  SlaRoutingPolicy = 'sla_routing_policy',
  SlaTaxExemption = 'sla_tax_exemption',
  ConversationTriageEnabled = 'conversation_triage_enabled',
  SmsSessionsOn = 'sms_sessions_on',
  SmsSessionIdleSeconds = 'sms_session_idle_seconds',
  SmsSessionNudgeAttempts = 'sms_session_nudge_attempts',
  SmsSessionNudgeCopy = 'sms_session_nudge_copy',
  SmsSessionGoodbyeCopy = 'sms_session_goodbye_copy',
  SignalAutoAssignmentDisabled = 'signal_auto_assignment_disabled',
  WorkflowEmergencyOverride = 'workflow_emergency_override',
}

const PARTNER_SETTING_VALUE_FIELDS = gql`
  fragment PartnerSettingValueFields on PartnerSettingValue {
    id
    partnerSettingValueId
    partnerId
    partnerSettingId
    value
    createdAt
    updatedAt
  }
`;

export const GET_PARTNER_SETTINGS_QUERY = gql`
  query GetPartnerSettingValue($params: QueryParams) @entity(name: "PartnerSettingValue") {
    getPartnerSettingValue(queryParams: $params) {
      records {
        ...PartnerSettingValueFields
      }
    }
  }
  ${PARTNER_SETTING_VALUE_FIELDS}
`;

const UPDATE_PARTNER_SETTINGS_VALUE = gql`
  mutation UpdatePartnerSettingValue(
    $conditions: [ConditionInput]
    $data: PartnerSettingValueDataInput
  ) {
    updatePartnerSettingValue(conditions: $conditions, data: $data) {
      ...PartnerSettingValueFields
    }
  }
  ${PARTNER_SETTING_VALUE_FIELDS}
`;

const CREATE_PARTNER_SETTINGS_VALUE = gql`
  mutation CreatePartnerSettingValue($data: PartnerSettingValueDataInput) {
    createPartnerSettingValue(data: $data) {
      ...PartnerSettingValueFields
    }
  }
  ${PARTNER_SETTING_VALUE_FIELDS}
`;

const DELETE_PARTNER_SETTINGS_VALUE = gql`
  mutation DeletePartnerSettingValue($conditions: [ConditionInput]) {
    deletePartnerSettingValue(conditions: $conditions) {
      ...PartnerSettingValueFields
    }
  }
  ${PARTNER_SETTING_VALUE_FIELDS}
`;

const useDefaultPartnerSettingFilters = (partnerId?: number | null) => {
  return useMemo(
    () => [
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [partnerId, PLATFORM_PARTNER_ID].filter(isNonNullish).join(','),
      },
      {
        attribute: 'partnerSettingId',
        operator: 'in',
        value: Object.values(PartnerSetting).join(','),
      },
    ],
    [partnerId],
  );
};

export interface GetPartnerSettingValueQuery {
  getPartnerSettingValue: Records<Partial<PartnerSettingValue>>;
}

export function usePartnerSettings(
  options?: QueryHookOptions<GetPartnerSettingValueQuery> & {
    partnerSetting?: PartnerSetting | PartnerSetting[];
    partnerId?: number | null;
    filters?: ConditionInput[];
  },
): {
  data: Partial<PartnerSettingValue>[];
  loading: boolean;
  error?: ApolloError;
};

export function usePartnerSettings(
  options?: QueryHookOptions<GetPartnerSettingValueQuery> & {
    partnerSetting?: PartnerSetting | PartnerSetting[];
    partnerId?: number | null;
    splitPartnerAndPlatformData?: true;
    filters?: ConditionInput[];
  },
): {
  data: {
    platformLevel: string[];
    partnerLevel: string[];
  };
  loading: boolean;
  error?: ApolloError;
};

export function usePartnerSettings(
  options?: QueryHookOptions<GetPartnerSettingValueQuery> & {
    partnerSetting?: PartnerSetting | PartnerSetting[];
    partnerId?: number | null;
    splitPartnerAndPlatformData?: boolean;
    filters?: ConditionInput[];
  },
): {
  data:
    | Partial<PartnerSettingValue>[]
    | {
        platformLevel: string[];
        partnerLevel: string[];
      };
  loading: boolean;
  error?: ApolloError;
} {
  const { partnerId, splitPartnerAndPlatformData, partnerSetting, filters, ...rest } =
    options || {};

  const defaultPartnerSettingFilters = useDefaultPartnerSettingFilters(partnerId);

  const { data, loading, error } = useQuery<GetPartnerSettingValueQuery, QueryVariables>(
    GET_PARTNER_SETTINGS_QUERY,
    {
      variables: {
        params: { filters: [...defaultPartnerSettingFilters, ...(filters ?? [])] },
      },
      context: {
        includePlatformLevelData: true,
      },
      ...rest,
    },
  );

  const settings = useMemo(() => {
    let records = data?.getPartnerSettingValue?.records || [];

    if (partnerSetting) {
      const selectionIds = Array.isArray(partnerSetting) ? partnerSetting : [partnerSetting];

      records = records.filter((i) =>
        selectionIds.includes(i.partnerSettingId as PartnerSetting),
      );
    }

    if (!splitPartnerAndPlatformData) {
      return records;
    }

    if (!records?.length) {
      return {
        platformLevel: [],
        partnerLevel: [],
      };
    }

    const platformLevel = records.find((record) => record.partnerId === PLATFORM_PARTNER_ID);
    const partnerLevel = records.find((record) => record.partnerId === partnerId);

    /**
     * Values parsing is hardcoded for now, since we use this option only for
     * the settings with values represented as comma-separated strings.
     * But in the future we may want to make use of `value_type` column in `partner_setting`
     * table to be able to distinguish between different types of values.
     */
    return {
      platformLevel: platformLevel?.value?.split(',').filter((item) => item) || [],
      partnerLevel: partnerLevel?.value?.split(',').filter((item) => item) || [],
    };
  }, [data, partnerId, splitPartnerAndPlatformData, partnerSetting]);

  return {
    data: settings,
    loading,
    error,
  };
}

export function useManagePartnerSettingsValue(partnerId: number | null) {
  const { showErrorAlert } = useNotifications();
  const defaultPartnerSettingFilters = useDefaultPartnerSettingFilters(partnerId);

  const [updatePartnerSettingsValueMutation] = useMutation<{
    updatePartnerSettingValue: PartnerSettingValue[];
  }>(UPDATE_PARTNER_SETTINGS_VALUE, {
    onError: (error) => {
      console.error(error);
      const message =
        'Something went wrong updating the partner settings value, please try again';
      showErrorAlert(message);
    },
  });

  const [createPartnerSettingsValueMutation] = useMutation<{
    createPartnerSettingValue: PartnerSettingValue[];
  }>(CREATE_PARTNER_SETTINGS_VALUE, {
    onError: (error) => {
      console.error(error);
      const message =
        'Something went wrong creating the partner settings value, please try again';
      showErrorAlert(message);
    },
  });

  const updatePartnerSettingsValue = useCallback(
    async (payload: Partial<PartnerSettingValue>, filters: ConditionInput[] = []) => {
      if (!payload.partnerSettingValueId) {
        return;
      }

      const optimisticResponseData = getPartnerSettingsValueOptimisticResponse(
        payload,
        partnerId!,
      );
      const { data } = await updatePartnerSettingsValueMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: payload.partnerSettingValueId,
            },
          ],
          data: {
            value: payload.value,
          },
        },
        optimisticResponse: {
          updatePartnerSettingValue: [optimisticResponseData],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updatePartnerSettingValue?.[0];
          if (!updated) return;

          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: { filters: [...defaultPartnerSettingFilters, ...filters] },
              },
            },
            (existingData) => {
              if (!existingData?.getPartnerSettingValue) return;

              const updatedPartnerSettingsValue =
                existingData.getPartnerSettingValue.records.map((item) => {
                  if (item.partnerSettingValueId === payload.partnerSettingValueId) {
                    return {
                      ...item,
                      value: payload.value,
                    };
                  }

                  return item;
                });
              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingDataResponse',
                  records: updatedPartnerSettingsValue,
                },
              };
            },
          );
        },
      });

      return data?.updatePartnerSettingValue[0];
    },
    [partnerId, updatePartnerSettingsValueMutation, defaultPartnerSettingFilters],
  );

  const [deletePartnerSettingsValueMutation] = useMutation<{
    deletePartnerSettingValue: PartnerSettingValue[];
  }>(DELETE_PARTNER_SETTINGS_VALUE, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong deleting the partner settings value, please try again',
      );
    },
  });

  const createPartnerSettingsValue = useCallback(
    async (payload: Partial<PartnerSettingValue>, filters: ConditionInput[] = []) => {
      if (!payload.partnerSettingId || !payload.value) {
        return;
      }

      const optimisticResponseData = getPartnerSettingsValueOptimisticResponse(
        payload,
        partnerId!,
      );

      const { data } = await createPartnerSettingsValueMutation({
        variables: {
          data: {
            value: payload.value,
            partnerId,
            partnerSettingId: payload.partnerSettingId,
          },
        },
        optimisticResponse: {
          createPartnerSettingValue: [optimisticResponseData],
        },
        update: (cache, { data: response }) => {
          const created = response?.createPartnerSettingValue?.[0];
          if (!created) return;
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: { filters: [...defaultPartnerSettingFilters, ...filters] },
              },
            },
            (existingData) => {
              if (!existingData) return;

              const existingRecords = existingData.getPartnerSettingValue?.records || [];
              const others = existingRecords.filter(
                (r) =>
                  !(
                    r.partnerSettingId === created.partnerSettingId &&
                    r.partnerId === created.partnerId
                  ),
              );

              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingDataResponse',
                  records: [...others, created],
                },
              };
            },
          );
        },
      });

      return data?.createPartnerSettingValue[0];
    },
    [createPartnerSettingsValueMutation, defaultPartnerSettingFilters, partnerId],
  );

  const deletePartnerSettingsValue = useCallback(
    async (partnerSettingValueId: string, filters: ConditionInput[] = []) => {
      const { data } = await deletePartnerSettingsValueMutation({
        variables: {
          conditions: [{ attribute: 'id', operator: '=', value: partnerSettingValueId }],
        },
        update: (cache) => {
          // Drop the row from the cached query list, then evict the orphan.
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: { filters: [...defaultPartnerSettingFilters, ...filters] },
              },
            },
            (existingData) => {
              if (!existingData?.getPartnerSettingValue) return;
              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingDataResponse',
                  records: existingData.getPartnerSettingValue.records.filter(
                    (r) => r.partnerSettingValueId !== partnerSettingValueId,
                  ),
                },
              };
            },
          );
          const cacheId = cache.identify({
            __typename: 'PartnerSettingValue',
            id: partnerSettingValueId,
          });
          if (cacheId) {
            cache.evict({ id: cacheId });
            cache.gc();
          }
        },
      });

      return data?.deletePartnerSettingValue[0];
    },
    [deletePartnerSettingsValueMutation, defaultPartnerSettingFilters],
  );

  return {
    updatePartnerSettingsValue,
    createPartnerSettingsValue,
    deletePartnerSettingsValue,
  };
}

export const usePartnerSetting = (
  partnerSetting: PartnerSetting,
  defaultToPlatformLevel = false,
) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();

  const { data, loading, error } = usePartnerSettings({ partnerId, partnerSetting });

  const partnerLevelSetting = data?.find(
    (setting) => setting.partnerId !== PLATFORM_PARTNER_ID,
  );

  const platformLevelSetting = data?.find(
    (setting) => setting.partnerId === PLATFORM_PARTNER_ID,
  );

  return {
    // if `defaultToPlatformLevel` is true, we return the platform-level setting
    // as a fallback (if present)
    data:
      (defaultToPlatformLevel
        ? (partnerLevelSetting ?? platformLevelSetting)
        : partnerLevelSetting) ?? data?.[0],
    loading,
    error,
  };
};

function getPartnerSettingsValueOptimisticResponse(
  payload: Partial<PartnerSettingValue>,
  partnerId: number,
): PartnerSettingValue {
  const createdAt = new Date().toISOString();
  const tempId = `temp-id${createdAt}`;

  return {
    partnerSettingValueId: payload.partnerSettingValueId || tempId,
    id: payload.id || tempId,
    partnerId,
    partnerSettingId: payload.partnerSettingId!,
    value: payload.value!,
    createdAt,
    updatedAt: createdAt,
    partner: payload.partner!,
    partnerSetting: payload.partnerSetting!,
  };
}

export function isSettingsEnabled(
  settingsIdToCheck: PartnerSetting,
  settingsObject: Pick<PartnerSettingValue, 'partnerSettingId' | 'value'>,
) {
  return (
    (settingsObject.partnerSettingId as PartnerSetting) === settingsIdToCheck &&
    isStringBooleanTrue(settingsObject.value)
  );
}
